import { useCallback, useEffect, useRef, useState } from "react";
import { getTablePrefs, saveTablePrefs } from "../services/uiPrefsApi";

/**
 * Personal UI settings of the signed-in user by key (/v1/ui-preferences/<key>).
 * Правка №4/№14 (Чат 1), [Claude] 2026-11-03 — same storage as useTablePrefs, but for any object.
 * normalize(raw) → full settings object; update(patch | fn) merges and saves with a short delay.
 */
const SAVE_DELAY_MS = 600;
const cacheKey = (key) => `hr:ui-prefs:${key}`;

function readCache(key) {
    try {
        const raw = window.localStorage.getItem(cacheKey(key));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCache(key, value) {
    try {
        window.localStorage.setItem(cacheKey(key), JSON.stringify(value));
    } catch {
        /* storage unavailable — the server copy is enough */
    }
}

export default function useUiPrefs(key, normalize = (value) => value || {}) {
    const normalizeRef = useRef(normalize);
    normalizeRef.current = normalize;
    const [prefs, setPrefs] = useState(() => normalize(readCache(key)));
    const [loaded, setLoaded] = useState(false);
    const prefsRef = useRef(prefs);
    const timer = useRef(null);
    const pending = useRef(null);

    useEffect(() => {
        let alive = true;
        Promise.resolve()
            .then(() => getTablePrefs(key))
            .then((server) => {
                if (!alive || pending.current) return;
                const next = normalizeRef.current(server);
                prefsRef.current = next;
                setPrefs(next);
                writeCache(key, next);
            })
            .catch(() => { /* keep cached/default settings */ })
            .finally(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, [key]);

    const flush = useCallback(async () => {
        const value = pending.current;
        if (!value) return;
        pending.current = null;
        try {
            await saveTablePrefs(key, value);
        } catch {
            /* next change retries; the browser copy keeps the choice meanwhile */
        }
    }, [key]);

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
        void flush();
    }, [flush]);

    const update = useCallback((patch) => {
        const current = prefsRef.current;
        const changes = typeof patch === "function" ? patch(current) : patch;
        const next = normalizeRef.current({ ...current, ...(changes || {}) });
        if (JSON.stringify(next) === JSON.stringify(current)) return;
        prefsRef.current = next;
        setPrefs(next);
        writeCache(key, next);
        pending.current = next;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; void flush(); }, SAVE_DELAY_MS);
    }, [key, flush]);

    return { prefs, update, loaded };
}
